import { Box, Typography, Button } from '@mui/material';
import { EventBroker } from 'BMapsSrc/eventbroker';
import { ResultsPaneEvents } from './dialog/ResultsPane/constants';

const GLYPH_CLEAN = '🟢';
const GLYPH_WARNING = '🟡';
const GLYPH_SEVERE = '🔴';
const GLYPH_UNAVAILABLE = '?';

function flagGlyph(flagsField) {
    if (flagsField === null || flagsField === undefined) return GLYPH_UNAVAILABLE;
    if (!Array.isArray(flagsField) || flagsField.length === 0) return GLYPH_CLEAN;

    let hasSevere = false;
    for (const f of flagsField) {
        const sev = typeof f === 'string' ? 'warning' : f && f.severity;
        if (sev === 'severe') {
            hasSevere = true;
            break;
        }
    }
    return hasSevere ? GLYPH_SEVERE : GLYPH_WARNING;
}

function formatScore(score) {
    if (score === null || score === undefined || !Number.isFinite(score)) return '—';
    return score.toFixed(1);
}

export default function ResultCard({ ranked, onOpenPane }) {
    if (!Array.isArray(ranked) || ranked.length === 0) return null;

    const rows = ranked.slice(0, 3);

    const handleOpen = () => {
        EventBroker.publish(ResultsPaneEvents.OPEN_COMPOUND, {
            compoundId: rows[0].compoundId,
        });
        if (typeof onOpenPane === 'function') onOpenPane();
    };

    return (
        <Box
            sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                px: 1,
                py: 0.75,
                my: 0.5,
                width: '100%',
                maxWidth: '420px',
                boxSizing: 'border-box',
            }}
        >
            <Box
                sx={{
                    display: 'flex', flexDirection: 'column', gap: 0.25, mb: 0.75,
                }}
            >
                {/* Column headers — without these the values (#1 0.0 ?) are
                    unlabeled and users can't tell score from status. Widths
                    match the value rows below so columns line up. */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        color: 'text.secondary',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        pb: 0.25,
                    }}
                >
                    <Typography component="span" sx={{ width: '2.5rem', fontSize: 'inherit' }}>
                        #
                    </Typography>
                    <Typography
                        component="span"
                        sx={{ flex: 1, textAlign: 'right', fontSize: 'inherit' }}
                    >
                        Score
                    </Typography>
                    <Typography
                        component="span"
                        title="Status / warnings"
                        sx={{ width: '1.5rem', textAlign: 'center', fontSize: 'inherit' }}
                    >
                        ⚠
                    </Typography>
                </Box>
                {rows.map((c) => (
                    <Box
                        key={c.compoundId}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            fontSize: '0.85rem',
                        }}
                    >
                        <Typography
                            component="span"
                            sx={{ width: '2.5rem', fontVariantNumeric: 'tabular-nums' }}
                        >
                            {`#${c.rank}`}
                        </Typography>
                        <Typography
                            component="span"
                            sx={{
                                flex: 1,
                                textAlign: 'right',
                                fontVariantNumeric: 'tabular-nums',
                            }}
                        >
                            {formatScore(c.primaryScore)}
                        </Typography>
                        <Typography
                            component="span"
                            sx={{ width: '1.5rem', textAlign: 'center' }}
                        >
                            {flagGlyph(c.flags)}
                        </Typography>
                    </Box>
                ))}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                    size="small"
                    variant="contained"
                    onClick={handleOpen}
                >
                    Open in Results Pane
                </Button>
            </Box>
        </Box>
    );
}
